'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getYoutubeThumbnailCandidates, isMissingYoutubeThumbnail } from '@/lib/storyblok/youtube-thumbnails'

import { readThumbnailWidth } from './read-thumbnail-width'

export const useYoutubeThumbnail = (videoId: string, thumbnail?: string) => {
  const candidates = useMemo(() => getYoutubeThumbnailCandidates(videoId, thumbnail), [videoId, thumbnail])
  const [index, setIndex] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const ref = useRef<HTMLImageElement | null>(null)
  const url = candidates[index] || candidates[0]
  const failed = useCallback(() => {
    if (index < candidates.length - 1) {
      setLoaded(false)
      setIndex(index + 1)
    } else {
      setLoaded(true)
    }
  }, [index, candidates.length])
  const finish = useCallback(() => {
    if (ref.current) {
      readThumbnailWidth(ref.current, (width) => {
        if (isMissingYoutubeThumbnail(url, width)) {
          failed()
        } else {
          setLoaded(true)
        }
      })
    }
  }, [url, failed])

  useEffect(() => {
    setIndex(0)
    setLoaded(false)
  }, [videoId, thumbnail])

  useEffect(() => {
    const img = ref.current
    if (!img?.complete) {
      return
    }
    if (img.naturalWidth > 0) {
      finish()
    } else {
      failed()
    }
  }, [url, finish, failed])

  return { url, ref, loaded, onLoad: finish, onError: failed }
}
